import ClearIcon from '@mui/icons-material/Clear';
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Theme, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useState } from 'react';
import { MassDmScheduleListSortBy } from '../../../../hooks/useMessageUsersMassDmScheduleList';
import SextforceColorSelector from '../../../common/SextforceColorSelector';
import StyledCard from '../../../common/StyledCard';

type Props = {
    scheduledMessagesLoading: boolean;
    queryScheduleActive: boolean;
    setQueryScheduleActive: (queryScheduleActive: boolean) => void;
    setQuery: (query: string) => void;
    sortBy: MassDmScheduleListSortBy;
    setSortBy: (sortBy: MassDmScheduleListSortBy) => void;
    colorId: string | undefined;
    setColorId: (colorId: string | undefined) => void;
    theme: Theme;
};

const MessageUsersAutoMassDmScheduleSearchBar = (props: Props) => {
    const {
        scheduledMessagesLoading,
        queryScheduleActive,
        setQueryScheduleActive,
        setQuery,
        colorId,
        setColorId,
        sortBy,
        setSortBy,
        theme,
    } = props;

    const [queryText, setQueryText] = useState<string>('');

    const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Add a small delay to avoid too many requests
        setTimeout(() => {
            setQuery(event.target.value);
        }, 500);

        setQueryText(event.target.value);
    };

    return (
        <StyledCard noCard noBottomMargin>
            <Box
                sx={{
                    bgcolor: 'grey.50',
                    p: 2,
                    // Elevation 1
                    boxShadow: theme.shadows[1],
                    borderRadius: theme.shape.borderRadius,
                }}
            >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12}>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item xs="auto">
                                    <ToggleButtonGroup
                                        color="primary"
                                        value={queryScheduleActive ? 'active' : 'inactive'}
                                        exclusive
                                        size="small"
                                        onChange={(event, value) => {
                                            if (value !== null) {
                                                setQueryScheduleActive(value === 'active');
                                            }
                                        }}
                                        sx={{
                                            mt: '4px',
                                            backgroundColor: theme.palette.background.paper,
                                        }}
                                    >
                                        <ToggleButton value={'active'}>Active</ToggleButton>
                                        <ToggleButton value={'inactive'}>Inactive</ToggleButton>
                                    </ToggleButtonGroup>
                                </Grid>

                                <Grid item xs>
                                    <TextField
                                        fullWidth
                                        label="Search"
                                        value={queryText}
                                        onChange={handleQueryChange}
                                        onKeyDown={e => {
                                            // Clear on Escape
                                            if (e.key === 'Escape') {
                                                setQuery('');
                                                setQueryText('');
                                            }
                                        }}
                                        InputProps={{
                                            endAdornment: queryText.length > 0 && (
                                                <ClearIcon
                                                    onClick={() => {
                                                        setQuery('');
                                                        setQueryText('');
                                                    }}
                                                    sx={{
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            ),
                                        }}
                                        sx={{
                                            backgroundColor: theme.palette.background.paper,
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs sm>
                            <SextforceColorSelector
                                colorId={colorId}
                                setColorId={setColorId}
                                allowEdit={false}
                                size="small"
                                noneName="Any"
                            />
                        </Grid>

                        <Grid item xs sm>
                            <FormControl fullWidth size="small" sx={{ mb: 0, mt: 0 }}>
                                <InputLabel id="sort-by-label">Sort By</InputLabel>
                                <Select
                                    labelId="sort-by-label"
                                    label="Sort By"
                                    fullWidth
                                    size="small"
                                    value={sortBy}
                                    onChange={e => {
                                        setSortBy(e.target.value as MassDmScheduleListSortBy);
                                    }}
                                    sx={{
                                        backgroundColor: theme.palette.background.paper,
                                    }}
                                >
                                    <MenuItem value={'_id'}>Date Added</MenuItem>
                                    <MenuItem value={'name'}>Name</MenuItem>
                                    <MenuItem value={'scheduleDate'}>Scheduled Date</MenuItem>
                                    <MenuItem value={'colorId'}>Color</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </LocalizationProvider>
            </Box>
        </StyledCard>
    );
};

export default MessageUsersAutoMassDmScheduleSearchBar;
